import React, { useState, useEffect } from "react";
import classNames from "classnames";
import moment from "moment";
import { Loading } from "components";
import { FormattedMessage, injectIntl } from "react-intl";
import messages from "i18n/messages";
import { Alert, Modal, ModalBody, Button } from "reactstrap";
import GiroPayLogo from "assets/img/giropay.png";
import EPSLogo from "assets/img/eps.png";
import CardLogo from "assets/img/card.png";
import CardSection from "./CardSection";
import IbanSection from "./IbanSection";
import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";

function FullpageLoadingModal({ isOpen = true }) {
  return (
    <Modal centered isOpen={isOpen}>
      <ModalBody>
        <div className="loading-wrap modal-loading-wrap">
          <Loading />
          <div className="text">
            <FormattedMessage {...messages.processingYourPayment} />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}

function CheckoutForm({
  clientSecret,
  customer,
  paymentData,
  order,
  org,
  intl,
}) {
  const initialState = {
    loading: false,
    error: null,
    status: null,
  };
  const { formatMessage } = intl;
  const [status, setStatus] = useState(initialState);
  const [showAllPaymentMethods, toggleShowAllPaymentMethods] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  const stripePaymentHeader = order
    ? `Tickets ${order.date_infos.title} ${moment(
        order.date_infos.starts_at
      ).format("DD. MMM. YYYY")}`
    : formatMessage(messages.voucher);

  const handleSetPaymentMethod = (index) => {
    toggleShowAllPaymentMethods(false);
    setActiveIndex(index);
  };

  const voucherOrDonation = (isDonation) =>
    isDonation ? "yourDonationReceipt" : "yourVoucher";

  const renderEmailMessage = (paymentData) => (
    <div>
      <FormattedMessage
        {...messages.findYourOrderInEmail}
        values={{
          order: formatMessage(
            messages[
              paymentData.is_order
                ? "yourTickets"
                : voucherOrDonation(paymentData.is_donation)
            ]
          ),
        }}
      />
    </div>
  );

  const renderPostMessage = (paymentData) => (
    <div>
      <FormattedMessage
        {...messages.findYourInvoiceInEmailRestViaPost}
        values={{
          order: formatMessage(
            messages[paymentData.is_order ? "yourTickets" : "yourVoucher"]
          ),
        }}
      />
    </div>
  );

  const renderBicycleMessage = (paymentData) => (
    <div>
      <FormattedMessage
        {...messages.findYourInvoiceInEmailRestViaBicycle}
        values={{
          order: formatMessage(
            messages[paymentData.is_order ? "yourTickets" : "yourVoucher"]
          ),
        }}
      />
    </div>
  );

  const renderCollectMessage = (paymentData) => (
    <div>
      <FormattedMessage
        {...messages.findYourInvoiceInEmailCollectRest}
        values={{
          order: formatMessage(
            messages[paymentData.is_order ? "yourTickets" : "yourVoucher"]
          ),
        }}
      />
    </div>
  );

  const renderDeliveryMessage = () => {
    const deliveryMethods = {
      pdf: renderEmailMessage,
      post: renderPostMessage,
      bicycle: renderBicycleMessage,
      collect: renderCollectMessage,
    };
    return paymentData && deliveryMethods[paymentData.delivery_method] ? (
      deliveryMethods[paymentData.delivery_method](paymentData)
    ) : (
      <div>
        unknown delivery method {paymentData && paymentData.delivery_method}
      </div>
    );
  };

  const renderErrorStatus = () => (
    <div className="feedback-check">
      <div className="text-danger">{status.error}</div>
    </div>
  );

  const renderSuccessStatus = () => (
    <div className="feedback-check">
      <div className="icon-wrap text-success">
        <span className="tt-icon tt-icon-party-popper" />
      </div>
      <div className="text">
        <FormattedMessage
          {...messages[
            paymentData.is_donation
              ? "thankYouForSupportingUs"
              : "thankYouForPayment"
          ]}
        />
        <div className="further-instructions">
          {renderDeliveryMessage(paymentData)}
        </div>
      </div>
    </div>
  );

  const dispatchRenderStatus = {
    succeeded: renderSuccessStatus,
    error: renderErrorStatus,
  };

  const renderUnknownStatus = () => (
    <div className="feedback-check">
      <Loading />
      <div className="text">
        <FormattedMessage {...messages.processingYourPayment} /> (redirect)
      </div>
    </div>
  );

  const paymentMethods = [
    {
      key: "card",
      logo: <img src={CardLogo} />,
      component: (
        <CardSection
          org={org}
          clientSecret={clientSecret}
          paymentData={paymentData}
          customer={customer}
          setStatus={setStatus}
        />
      ),
    },
    {
      key: "eps",
      logo: <img src={EPSLogo} />,
      component: (
        <IbanSection
          org={org}
          clientSecret={clientSecret}
          paymentData={paymentData}
          customer={customer}
          setStatus={setStatus}
          paymentMethod="eps"
        />
      ),
    },
    {
      key: "giropay",
      logo: <img src={GiroPayLogo} />,
      component: (
        <IbanSection
          org={org}
          clientSecret={clientSecret}
          paymentData={paymentData}
          customer={customer}
          setStatus={setStatus}
          paymentMethod="giropay"
        />
      ),
    },
  ];

  const renderPaymentMethods = () => (
    <div className="payment-methods">
      <nav
        className={classNames("payment-methods-header", {
          show: showAllPaymentMethods,
        })}
      >
        <div className="cur-payment-method">
          {paymentMethods[activeIndex].logo}
          <button
            className="change-payment-method"
            onClick={() => toggleShowAllPaymentMethods(!showAllPaymentMethods)}
          >
            <span className="mobile">
              <FormattedMessage {...messages.paymentMethods} />
            </span>
            <span className="desktop">
              <FormattedMessage {...messages.otherPaymentMethods} />
            </span>
            <span className="tt-icon tt-icon-down-arrow" />
          </button>
        </div>
        <ul className="methods-list">
          {paymentMethods.map((m, index) => (
            <li key={`pm-${index}`}>
              <button onClick={() => handleSetPaymentMethod(index)}>
                <div className="payment-method-img">{m.logo}</div>
                <div className="payment-method-text">
                  <FormattedMessage {...messages[m.key]} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="payment-method-content">
        {paymentMethods[activeIndex].component}
      </div>
    </div>
  );

  //useEffect(() => {
  //if (stripe) {
  //const pr = stripe.paymentRequest({
  //country: org.addr.country_code || "AT",
  //currency: org.currency.toLowerCase(),
  //total: {
  //label: stripePaymentHeader,
  //amount: paymentData.value,
  //},
  //requestPayerName: true,
  //requestPayerEmail: true,
  //});

  //pr.canMakePayment().then((result) => {
  //if (result) {
  //setPaymentRequest(pr);
  //}
  //});
  //}
  //}, [stripe]);

  //if (paymentRequest) {
  //paymentRequest.on("paymentmethod", async (ev) => {
  //// Confirm the PaymentIntent without handling potential next actions (yet).
  //const { error: confirmError } = await stripe.confirmCardPayment(
  //clientSecret,
  //{ payment_method: ev.paymentMethod.id },
  //{ handleActions: false }
  //);

  //if (confirmError) {
  //// Report to the browser that the payment failed, prompting it to
  //// re-show the payment interface, or show an error message and close
  //// the payment interface.
  //ev.complete("fail");
  //} else {
  //// Report to the browser that the confirmation was successful, prompting
  //// it to close the browser payment method collection interface.
  //ev.complete("success");
  //// Let Stripe.js handle the rest of the payment flow.
  //const { error, paymentIntent } = await stripe.confirmCardPayment(
  //clientSecret
  //);
  //if (error) {
  //// The payment failed -- ask your customer for a new payment method.
  //} else {
  //// The payment has succeeded.
  //}
  //}
  //});
  //return <PaymentRequestButtonElement options={{ paymentRequest }} />;
  //}

  return (
    <div className="stripe-form">
      {status.status === "succeeded" ? null : renderPaymentMethods()}
      {status.status ? (
        <Alert color="danger">
          {dispatchRenderStatus[status.status]
            ? dispatchRenderStatus[status.status]()
            : renderUnknownStatus()}
        </Alert>
      ) : null}
      {status.loading && <FullpageLoadingModal />}
    </div>
  );
}

export default injectIntl(CheckoutForm);
